import React, { useCallback, useEffect, useState } from 'react';
import { FaPlus, FaRedo } from 'react-icons/fa';
import Btn from '../../components/ui/Btn';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import StatusTag from '../../components/ui/StatusTag';
import Field, { TextInput } from '../../components/ui/Field';
import { employeesService, tempPassword } from '../../services/employeesService';
import { friendlyError } from '../../services/agentsService';
import { toastSuccess, toastError } from '../../utils/toast';

/** Equipo: las personas que entran a la plataforma con acceso solo a Agentes (empleados). Los administradores van en «Administradores». */
export default function AdminEquipoPage() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [target, setTarget] = useState(null);       // { kind: 'password' | 'delete' | 'toggle', row }

  const load = useCallback(async () => {
    setState('loading');
    try { setRows(await employeesService.list()); setState('ready'); }
    catch (e) { setError(/employee_list|schema cache|does not exist/i.test(e.message || '') ? 'Falta correr el script 47 en la base de datos.' : friendlyError(e)); setState('error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-5xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-[34px] sm:text-[40px] leading-[1.05] font-bold text-fg">Equipo</h1>
          <p className="mt-2 text-[15px] text-fg-dim max-w-[62ch]">Empleados que entran a la plataforma solo para trabajar con agentes: ven los públicos y los que ellos crean, nunca los privados de otras personas, ni clientes ni administración.</p>
        </div>
        <Btn variant="primary" size="lg" icon={FaPlus} onClick={() => setCreating(true)}>Nuevo empleado</Btn>
      </header>

      {state === 'error' ? (
        <div role="alert" className="rounded-3xl border border-signal/30 bg-signal-soft p-6 text-sm text-fg">
          <p className="font-semibold">No se pudo cargar el equipo.</p><p className="mt-1 text-fg-dim">{error}</p>
          <Btn size="sm" className="mt-3" onClick={load}>Reintentar</Btn>
        </div>
      ) : state === 'loading' ? (
        <div className="space-y-3" aria-busy="true" aria-label="Cargando equipo">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-2xl skeleton opacity-50" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-fg/15 px-6 py-14 text-center">
          <p className="font-display text-[20px] font-bold text-fg">Todavía no hay empleados</p>
          <p className="mt-1 text-sm text-fg-dim">Crea el primero: recibe un correo y una contraseña temporal para entrar.</p>
        </div>
      ) : (
        <ul className="rounded-3xl bg-panel ring-1 ring-line divide-y divide-line overflow-hidden">
          {rows.map((r) => (
            <li key={r.auth_user_id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 font-semibold text-fg"><span className="truncate">{r.full_name}</span>{r.is_active ? <StatusTag tone="live" dot>Activo</StatusTag> : <StatusTag>Desactivado</StatusTag>}</p>
                <p className="text-[13px] text-fg-dim truncate">{r.email} · {r.agents} {r.agents === 1 ? 'agente' : 'agentes'}{r.project_name ? ` · ${r.project_name}` : ''}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Btn size="sm" variant="ghost" onClick={() => setTarget({ kind: 'password', row: r })}>Contraseña</Btn>
                <Btn size="sm" variant="ghost" onClick={() => setTarget({ kind: 'toggle', row: r })}>{r.is_active ? 'Desactivar' : 'Activar'}</Btn>
                <Btn size="sm" variant="ghost" onClick={() => setTarget({ kind: 'delete', row: r })}>Eliminar</Btn>
              </div>
            </li>
          ))}
        </ul>
      )}

      <NewEmployeeModal open={creating} onClose={() => setCreating(false)} onDone={load} />
      <PasswordModal row={target?.kind === 'password' ? target.row : null} onClose={() => setTarget(null)} />
      <ConfirmModal open={target?.kind === 'toggle'} onClose={() => setTarget(null)} title={target?.row?.is_active ? 'Desactivar al empleado' : 'Activar al empleado'}
        confirmLabel={target?.row?.is_active ? 'Desactivar' : 'Activar'} tone={target?.row?.is_active ? 'danger' : undefined}
        onConfirm={async () => { try { await employeesService.setActive(target.row.auth_user_id, !target.row.is_active); toastSuccess(target.row.is_active ? 'Empleado desactivado' : 'Empleado activado'); await load(); } catch (e) { toastError(friendlyError(e)); throw e; } }}>
        <p>{target?.row?.is_active ? `${target?.row?.full_name} ya no podrá ver ni editar agentes. Sus agentes se conservan.` : `${target?.row?.full_name} vuelve a entrar y a ver sus agentes.`}</p>
      </ConfirmModal>
      <ConfirmModal open={target?.kind === 'delete'} onClose={() => setTarget(null)} title="Eliminar al empleado" confirmLabel="Eliminar" tone="danger"
        onConfirm={async () => { try { await employeesService.remove(target.row.auth_user_id); toastSuccess('Empleado eliminado'); await load(); } catch (e) { toastError(e.message); throw e; } }}>
        <p>Se borra su acceso a la plataforma. Sus agentes y su proyecto interno se conservan: un administrador puede verlos y moverlos al proyecto del cliente.</p>
      </ConfirmModal>
    </div>
  );
}

function NewEmployeeModal({ open, onClose, onDone }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);   // { email, password }
  useEffect(() => { if (open) { setName(''); setEmail(''); setPassword(tempPassword()); setTouched(false); setError(''); setDone(null); } }, [open]);

  const nameError = touched && name.trim().length < 2 ? 'Escribe el nombre completo.' : '';
  const emailError = touched && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) ? 'Escribe un correo válido.' : '';
  const passError = touched && password.length < 8 ? 'Mínimo 8 caracteres.' : '';
  const submit = async (e) => {
    e?.preventDefault(); setTouched(true);
    if (name.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) || password.length < 8) return;
    setBusy(true); setError('');
    try { await employeesService.create({ fullName: name.trim(), email: email.trim().toLowerCase(), password }); setDone({ email: email.trim().toLowerCase(), password }); onDone?.(); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Modal open={open} onClose={onClose} title="Empleado creado" description="Compártele estos datos por un medio seguro. La contraseña ya no se vuelve a mostrar."
        footer={<Btn variant="primary" onClick={onClose}>Listo</Btn>}>
        <dl className="space-y-2 text-[14px]">
          <div><dt className="text-fg-dim text-[12.5px]">Correo</dt><dd className="font-semibold text-fg break-all">{done.email}</dd></div>
          <div><dt className="text-fg-dim text-[12.5px]">Contraseña temporal</dt><dd className="font-mono text-fg break-all select-all">{done.password}</dd></div>
        </dl>
        <p className="mt-3 text-[13px] text-fg-faint">Puede cambiarla desde «Mi perfil» al entrar.</p>
      </Modal>
    );
  }
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Nuevo empleado" description="Entra con su correo y una contraseña temporal. Solo verá la sección Agentes."
      footer={<><Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn><Btn variant="primary" onClick={submit} loading={busy}>Crear empleado</Btn></>}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Nombre completo" error={nameError}><TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" data-autofocus /></Field>
        <Field label="Correo" error={emailError}><TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} autoComplete="off" /></Field>
        <Field label="Contraseña temporal" error={passError}
          adornment={<Btn size="sm" variant="ghost" icon={FaRedo} onClick={() => setPassword(tempPassword())} aria-label="Generar otra contraseña" type="button" />}>
          <TextInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" className="font-mono" />
        </Field>
        <p className="text-[12.5px] leading-relaxed text-fg-faint">Se crea también su proyecto interno, donde vivirán sus agentes y clones hasta que un administrador los pase al proyecto del cliente.</p>
        {error && <p role="alert" className="text-sm text-signal">{error}</p>}
        <button type="submit" className="sr-only" tabIndex={-1}>Crear empleado</button>
      </form>
    </Modal>
  );
}

function PasswordModal({ row, onClose }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [shown, setShown] = useState(false);
  useEffect(() => { if (row) { setPassword(tempPassword()); setError(''); setShown(false); } }, [row]);
  const save = async () => {
    if (password.length < 8) { setError('Mínimo 8 caracteres.'); return; }
    setBusy(true); setError('');
    try { await employeesService.resetPassword(row.auth_user_id, password); setShown(true); toastSuccess('Contraseña cambiada'); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={Boolean(row)} onClose={busy ? undefined : onClose} title={`Contraseña de ${row?.full_name || ''}`}
      description={shown ? 'Compártela por un medio seguro; ya no se vuelve a mostrar.' : 'Escribe una nueva o usa la generada.'}
      footer={shown ? <Btn variant="primary" onClick={onClose}>Listo</Btn> : <><Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn><Btn variant="primary" onClick={save} loading={busy}>Cambiar contraseña</Btn></>}>
      {shown
        ? <p className="font-mono text-[14px] text-fg break-all select-all">{password}</p>
        : <Field label="Nueva contraseña" error={error}><TextInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" className="font-mono" data-autofocus /></Field>}
    </Modal>
  );
}
